import {
  faArrowLeft as ArrowLeftIcon,
  faPen as EditIcon
} from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import { useColorScheme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import type { AxiosError } from 'axios';
import classNames from 'classnames';
import { useAtom } from 'jotai';
import { useNavigate, useParams } from 'react-router';

import { CheckPermissions } from '@/components/CheckPermissions';
import ExportButtonV2 from '@/components/ExportButtonV2';
import { triggerFileDownload } from '@/components/ExportButtonV2/ExportButtonV2.utils';
import { PERMISSIONS } from '@/constants/auth.constants';
import { useHandleError } from '@/hooks/useHandleError';
import { selectedOrganizationAtom } from '@/pages/Organizations';
import { ticketAtom } from '@/pages/Ticket/Ticket.store';
import type { PriorityDTO } from '@/pages/Ticket/Ticket.types';
import { useFetchTicketExport } from '@/pages/Ticket/TicketDetails/TicketDetails.store';
import { isLayoutMobileAtom } from '@/store/layout';

import TicketDetailsMoreButton from '../TicketDetailsMoreButton';
import TicketDetailsStatusButton from '../TicketDetailsStatusButton';
import useStyles from './TicketDetailsSubTopbar.styles';

const TicketDetailsSubTopbarTop = () => {
  const [isLayoutMobile] = useAtom(isLayoutMobileAtom);
  const [selectedOrganization] = useAtom(selectedOrganizationAtom);
  const [ticket] = useAtom(ticketAtom);
  const { mode: themeMode } = useColorScheme();
  const navigate = useNavigate();
  const { id } = useParams();
  const {
    organization,
    id: ticket_id,
    ticket_priority,
    ticket_type,
    title
  } = ticket || {};
  const { mutateAsync: fetchTicketExport } = useFetchTicketExport();
  const handleError = useHandleError();

  const priority = ticket_priority as PriorityDTO;
  const { classes } = useStyles({
    isLayoutMobile,
    priority,
    themeMode
  });

  const handleBack = () => {
    navigate({
      pathname: '/tickets'
    });
  };

  const handleEdit = () => {
    navigate(`/ticket/edit/${id}`);
  };

  const handleExport = async (format: 'json' | 'csv') => {
    try {
      const response = await fetchTicketExport({
        format,
        id: ticket_id
      });

      const mimeType =
        format === 'csv' ? 'text/csv;charset=utf-8' : 'application/json';
      const blob = new Blob([response.data], { type: mimeType });
      triggerFileDownload(blob, format, `ticket-${ticket_id}-export`);
    } catch (error) {
      handleError(error as AxiosError, 'Failure to export ticket details.');
    }
  };

  return (
    <div
      className={classNames(
        'ticketDetailsSubTopbar',
        classes.ticketDetailsSubTopbar
      )}
    >
      <div className={classes.subTopbarTopContent}>
        <Stack sx={{ alignItems: 'start' }} direction="row" spacing={1}>
          <div className={classes.topContentPriority}>
            {`P${ticket_priority?.id}`}
          </div>
          <div>
            <Typography
              variant="h6"
              style={{ display: 'inline', fontWeight: 'normal' }}
            >
              {`${
                ticket_type === 1 ? 'Threat' : 'Service'
              } Ticket #${ticket_id}`}

              {selectedOrganization.id !== organization?.id && (
                <>
                  {' '}
                  for{' '}
                  <Typography
                    className={classes.topContentOrganizationName}
                    sx={{ color: 'warning.dark' }}
                  >
                    {organization?.name}
                  </Typography>
                </>
              )}
            </Typography>
            <Typography component="h1" variant="h3">
              {title}
            </Typography>
          </div>
        </Stack>
        <Stack direction="row" sx={{ alignItems: 'center' }} spacing={1}>
          {isLayoutMobile && (
            <Button color="primary" variant="outlined" onClick={handleBack}>
              <FontAwesomeIcon icon={ArrowLeftIcon} size="xl" />
            </Button>
          )}
          <CheckPermissions permissions={[PERMISSIONS.TICKETS_UPDATE]}>
            <TicketDetailsStatusButton />
          </CheckPermissions>
          <CheckPermissions
            permissions={[PERMISSIONS.INTERNAL, PERMISSIONS.TICKETS_UPDATE]}
          >
            <Button
              color="primary"
              variant="contained"
              startIcon={<FontAwesomeIcon icon={EditIcon} size="xs" />}
              sx={{ whiteSpace: 'nowrap' }}
              onClick={handleEdit}
              {...(isLayoutMobile && { fullWidth: true })}
            >
              Edit Ticket
            </Button>
          </CheckPermissions>
          {ticket_id && <ExportButtonV2 handleClick={handleExport} json />}
          <TicketDetailsMoreButton />
        </Stack>
      </div>
    </div>
  );
};

export default TicketDetailsSubTopbarTop;
